import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { Suspense } from 'react';
import ReactModal from 'react-modal';
import { styled, useTheme, css } from 'styled-components';
import Icons from '../../Elements/Icons';
import Loader from '../../Elements/Loader';
import { Media } from '../../theme/Media';
const DEFAULT_ZINDEX = 1000;
export const MODAL_ANIMATION_DURATION_MS = 300;
ReactModal.setAppElement('body');
if (ReactModal.defaultStyles.overlay) {
    ReactModal.defaultStyles.overlay.zIndex = DEFAULT_ZINDEX;
}
const StyledModal = styled(ReactModal) `
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: ${({ styled: { height } }) => height};
  width: ${({ styled: { width } }) => width};
  outline: none;
  background: ${({ theme }) => theme.colors.grey00_1};
  z-index: 15;
  border-radius: 3px;
  ${({ theme }) => theme.shadows.medium}
  display: flex;
  flex-direction: column;
  &.ReactModal__Content {
    transform: translate(0, -40px);
    transition:
      transform ${MODAL_ANIMATION_DURATION_MS}ms,
      opacity ${MODAL_ANIMATION_DURATION_MS}ms;
    opacity: 0;
    &--after-open {
      transform: translate(0, 0);
      opacity: 1;
    }
    &--before-close {
      transform: translate(0, -40px);
      opacity: 0;
      pointer-events: none;
    }
  }
  @media ${Media.smMin} {
    margin: 40px auto;
    max-width: calc(100% - 80px);
  }
  @media ${Media.xsMax} {
    margin: 15px auto;
    max-width: calc(100% - 30px);
  }
`;
const Header = styled.div `
  color: ${({ theme }) => theme.colors.grey00_5};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }) => (theme.themeId === 'primaryDark' ? theme.colors.grey00_1 : theme.colors.black_1)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey10_1};
  padding: 8px 25px;
  position: relative;
  word-break: break-word;
  @media ${Media.smMin} {
    padding: 8px 25px;
  }
  @media ${Media.xsMax} {
    padding: 8px 15px;
  }
`;
const Actions = styled.div `
  display: flex;
  align-items: center;
  margin-left: 20px;
  & > * {
    &:not(:last-child) {
      margin-right: 10px;
    }
  }
  & > div {
    cursor: pointer;
    &:not(:last-child) {
      margin-right: 10px;
    }
  }
`;
const Title = styled.p `
  margin: 0;
  font-size: 16px;
`;
const CloseButton = styled.div `
  display: flex;
  align-items: center;
  padding: 10px 12px;
  margin-right: -14px;
`;
const ModalBodyCover = styled.div ``;
const ModalBody = styled.div `
  padding: 15px;
  position: relative;
`;
const ModalFooter = styled.div `
  padding: 15px;
  text-align: right;
  border-top: 1px solid ${({ theme }) => theme.colors.grey10_1};
  ${({ theme }) => theme.themeId === 'primaryDark' &&
    css `
      background-color: ${theme.colors.grey01_1};
    `}
`;
const Modal = ({ children, title, height = 'auto', width = '600px', actions = [], shouldCloseOnOverlayClick = true, zIndex = DEFAULT_ZINDEX, ...props }) => {
    const theme = useTheme();
    return (_jsx(StyledModal, { shouldCloseOnOverlayClick: shouldCloseOnOverlayClick, styled: {
            height,
            width,
        }, closeTimeoutMS: 300, style: { overlay: { zIndex, backgroundColor: theme.themeId === 'primaryDark' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.75)' } }, ...props, children: _jsxs(Suspense, { fallback: _jsx(Loader, {}), children: [_jsxs(Header, { children: [_jsx(Title, { children: title && title }), _jsxs(Actions, { children: [actions, _jsx(CloseButton, { onClick: (e) => props.onRequestClose && props.onRequestClose(e), "data-testid": 'close-button-modal-window', children: _jsx(Icons.Close, { size: 16, color: theme.colors.grey00_5 }) })] })] }), _jsx(ModalBodyCover, { "data-test": 'modal-window', children: children })] }) }));
};
const ModalMemo = React.memo(Modal);
const ModalWrapper = (props) => _jsx(ModalMemo, { ...props });
ModalWrapper.Body = ModalBody;
ModalWrapper.Footer = ModalFooter;
export default ModalWrapper;
